// Gating modal that asks the user — once per practice — whether they
// want the calm narrator voice to read each task aloud as they move
// through the flow. Renders inside an unclosable shadcn Dialog so the
// user has to make a deliberate pick before continuing.
//
// Choice is persisted to localStorage under a per-practice key so the
// modal only appears the first time the user opens a given practice.
// The choice ALSO writes through to the global `bc_narrate_tasks`
// preference so the existing auto-narrate effect in InterventionFlow
// turns on/off without any other plumbing changes.

import { Headphones, VolumeX, Settings as SettingsIcon } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";

const STORAGE_PREFIX = "bc_narration_choice_";

export const narrationChoiceKey = (practiceSlug) => `${STORAGE_PREFIX}${practiceSlug}`;

export const hasNarrationChoiceFor = (practiceSlug) => {
  if (!practiceSlug) return true;
  try {
    return Boolean(localStorage.getItem(narrationChoiceKey(practiceSlug)));
  } catch {
    return true; // private mode — don't pester the user with a modal we can't persist
  }
};

export const saveNarrationChoiceFor = (practiceSlug, choice) => {
  if (!practiceSlug) return;
  try {
    localStorage.setItem(narrationChoiceKey(practiceSlug), choice);
  } catch { /* ignore */ }
};

export const NarrationChoiceModal = ({
  open,
  practiceTitle,
  onChoose,
}) => (
  <Dialog open={open}>
    <DialogContent
      className="max-w-md p-0 overflow-hidden rounded-3xl border-0 shadow-[0_24px_80px_rgba(27,35,73,0.25)] [&>button]:hidden"
      onPointerDownOutside={(e) => e.preventDefault()}
      onEscapeKeyDown={(e) => e.preventDefault()}
      data-testid="narration-choice-modal"
    >
      <div className="bg-gradient-to-br from-[#3090A8] to-[#1B2349] text-white px-7 pt-8 pb-7">
        <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center mb-4 shadow-inner">
          <Headphones className="w-7 h-7" />
        </div>
        <DialogTitle className="font-outfit font-semibold text-2xl leading-tight">
          How would you like to do {practiceTitle || "this practice"}?
        </DialogTitle>
        <DialogDescription className="text-white/85 mt-2 leading-relaxed text-[15px]">
          A calm voice can read each task aloud as you move through the practice — so you can close your eyes and listen. You can change this any time in <span className="font-medium">Settings</span> (volume too).
        </DialogDescription>
      </div>

      <div className="bg-white px-7 pt-6 pb-7 space-y-3">
        <Button
          onClick={() => onChoose("on")}
          className="w-full justify-start rounded-2xl bg-[#1B2349] hover:bg-[#0F1838] text-white py-6 text-left shadow-sm"
          data-testid="narration-choice-on-btn"
        >
          <Headphones className="w-5 h-5 mr-3" />
          <div className="flex-1">
            <div className="font-outfit font-semibold text-[15px]">With narration</div>
            <div className="text-white/75 text-xs font-normal mt-0.5">Each task plays automatically when you move forward.</div>
          </div>
        </Button>

        <Button
          onClick={() => onChoose("off")}
          variant="outline"
          className="w-full justify-start rounded-2xl border-slate-200 hover:bg-[#FAFBFC] py-6 text-left"
          data-testid="narration-choice-off-btn"
        >
          <VolumeX className="w-5 h-5 mr-3 text-[#4A5568]" />
          <div className="flex-1">
            <div className="font-outfit font-semibold text-[15px] text-[#1B2349]">Without narration</div>
            <div className="text-[#718096] text-xs font-normal mt-0.5">Read each task at your own pace. You can still tap to listen.</div>
          </div>
        </Button>

        <p className="text-xs text-[#718096] pt-2 leading-relaxed flex items-start gap-1.5">
          <SettingsIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>Toggle narration on or off — and adjust volume — any time from Settings.</span>
        </p>
      </div>
    </DialogContent>
  </Dialog>
);

export default NarrationChoiceModal;
